if (!customElements.get('product-form-component')) {
  customElements.define(
    'product-form-component',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.variantIdInput.disabled = false;
        this.captureExternalSubmitter = this.captureExternalSubmitter.bind(this);
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        document.addEventListener('pointerdown', this.captureExternalSubmitter, true);
        document.addEventListener('click', this.captureExternalSubmitter, true);
        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.form?.querySelector('.add-to-cart-button');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        this.checkbox = this.form?.querySelector('[id^="agree_condition-"]');
        this.buyItNowButton = this.form?.querySelector('.shopify-payment-button, [data-buy-now-button]');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.initAgreeCondition();
        this.syncInitialSubmitState();

        // Sync error messages to sticky add-to-cart error area (same section)
        this.setupErrorSync();
      }

      syncInitialSubmitState() {
        if (!this.submitButton) return;

        const productInfo = this.closest('product-info');
        const selectedVariantScript = productInfo?.querySelector('variant-selects [data-selected-variant]');
        if (!selectedVariantScript?.textContent) return;

        let selectedVariant;
        try {
          selectedVariant = JSON.parse(selectedVariantScript.textContent);
        } catch (e) {
          return;
        }

        if (!selectedVariant) return;

        const sellingPlanInput = this.form?.querySelector('[name="selling_plan"]');
        const needsSellingPlan = selectedVariant.requires_selling_plan && !sellingPlanInput?.value;

        if (selectedVariant.available && !needsSellingPlan) {
          this.toggleSubmitButton(false);
          const quantityInput = this.form?.querySelector('.quantity__input');
          const inventoryQuantity = Number(quantityInput?.getAttribute('data-inventory-quantity'));
          const inventoryPolicy = quantityInput?.getAttribute('data-inventory-policy');
          if (inventoryQuantity <= 0 && inventoryPolicy === 'continue' && this.submitButtonText) {
            this.submitButtonText.textContent = window.variantStrings?.preOrder || this.submitButtonText.textContent;
          }
        } else if (!selectedVariant.available) {
          this.toggleSubmitButton(true, window.variantStrings?.soldOut);
        }
      }

      setupErrorSync() {
        try {
          const productInfo = this.closest('product-info');
          const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
          if (!sectionId) return;

          const stickyATC = document.querySelector(`sticky-atc[data-sticky-section-id="${sectionId}"]`);
          if (!stickyATC) return;

          const mainErrorWrapper = this.querySelector('.product-form__error-message-wrapper');
          const stickyErrorWrapper = stickyATC.querySelector('.product-form__error-message-wrapper');
          if (!mainErrorWrapper || !stickyErrorWrapper) return;

          const syncFromMain = () => {
            const isVisible = !mainErrorWrapper.hasAttribute('hidden');
            const errorMessage = mainErrorWrapper.querySelector('.product-form__error-message')?.textContent;
            stickyErrorWrapper.toggleAttribute('hidden', !isVisible || !errorMessage);
            if (isVisible && errorMessage) {
              const stickyErrorMessage = stickyErrorWrapper.querySelector('.product-form__error-message');
              if (stickyErrorMessage) stickyErrorMessage.textContent = errorMessage;
            }
          };

          // Initial sync
          syncFromMain();

          // Observe changes on main error wrapper
          this._errorObserver = new MutationObserver(syncFromMain);
          this._errorObserver.observe(mainErrorWrapper, {
            attributes: true,
            attributeFilter: ['hidden'],
            childList: true,
            subtree: true,
          });

          // Also listen to cartError pub/sub to copy message
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
            syncFromMain();
          });
        } catch (e) {
          // silent
        }
      }

      initAgreeCondition() {
        if (!this.checkbox || !this.buyItNowButton) return;

        this.buyItNowButton.classList.add('disabled');
        if (this.buyItNowButton.matches('[data-buy-now-button]')) {
          this.buyItNowButton.setAttribute('aria-disabled', true);
          this.buyItNowButton.setAttribute('disabled', 'disabled');
        }

        this.checkbox.addEventListener('change', () => {
          this.buyItNowButton.classList.toggle('disabled', !this.checkbox.checked);
          if (this.buyItNowButton.matches('[data-buy-now-button]')) {
            this.buyItNowButton.toggleAttribute('aria-disabled', !this.checkbox.checked);
            this.buyItNowButton.toggleAttribute('disabled', !this.checkbox.checked);
          }
        });

        this.form.addEventListener('submit', (e) => {
          if (!this.checkbox.checked) {
            e.preventDefault();
          }
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.isSubmitting = true;
        this.clearImmediateFeedbackTimer();
        const submitter = evt.submitter instanceof HTMLElement
          ? evt.submitter
          : this.getRecentExternalSubmitter()
            ? this.getRecentExternalSubmitter()
          : document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        const isBuyNowSubmit = submitter?.matches('[data-buy-now-button]');
        if (isBuyNowSubmit && this.checkbox && !this.checkbox.checked) {
          this.dispatchCartTrackingEvent('evolova:cart-add-error', {
            reason: 'buy_now_terms_unchecked',
            sourceArea: 'buy_now',
            target: submitter,
            isBuyNow: true,
          });
          this.finishBlockedSubmit(submitter, isBuyNowSubmit);
          return;
        }

        const activeSubmitButton = isBuyNowSubmit ? submitter : (submitter || this.submitButton);
        const activeSpinner = activeSubmitButton?.querySelector('.loading__spinner');

        if (!activeSubmitButton || activeSubmitButton.getAttribute('aria-disabled') === 'true') {
          this.dispatchCartTrackingEvent('evolova:cart-add-error', {
            reason: activeSubmitButton ? 'aria_disabled_button' : 'missing_submit_button',
            sourceArea: this.getTrackingSourceArea(activeSubmitButton, isBuyNowSubmit),
            target: activeSubmitButton,
            isBuyNow: isBuyNowSubmit,
          });
          this.finishBlockedSubmit(activeSubmitButton, isBuyNowSubmit);
          return;
        }

        this.error = false;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const properties = document.querySelectorAll('.product-form__input [name^="properties"]')

        properties.forEach(property => {
          if (property.type === 'file') {
            if (!property.files || property.files.length === 0 || !property.files[0]) return;
            formData.append(property.name, property.files[0]);
            return;
          }

          if (property.value == null || property.value === '') return;
          formData.append(property.name, property.value);
        })

        config.body = formData;
        let redirectingToCheckout = false;
        const cartTrackingBase = {
          form: this.form,
          target: activeSubmitButton,
          variantId: formData.get('id'),
          quantity: formData.get('quantity') || 1,
          sourceArea: this.getTrackingSourceArea(activeSubmitButton, isBuyNowSubmit),
          isBuyNow: isBuyNowSubmit,
          actionId: this.createCartActionId(formData.get('id')),
        };

        this.dispatchCartTrackingEvent('evolova:cart-add-click', cartTrackingBase);

        this.handleErrorMessage();

        activeSubmitButton.setAttribute('aria-disabled', true);
        activeSubmitButton.setAttribute('aria-busy', 'true');
        activeSubmitButton.classList.add('loading');
        this.setSubmitButtonBusyState(activeSubmitButton, true);
        activeSpinner?.classList.remove('hidden');

        // Also reflect loading state on the sticky add-to-cart button (if present)
        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
        const stickyAddButtons = this.getStickySubmitButtons(sectionId);
        if (!isBuyNowSubmit) this.setSubmitButtonsBusyState(stickyAddButtons, true);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const errorReason = response.message || response.description || 'shopify_cart_add_error';
              const shouldLockButton = this.isInventoryLockError(response);
              this.dispatchCartTrackingEvent('evolova:cart-add-error', {
                ...cartTrackingBase,
                response,
                reason: errorReason,
                message: response.description || response.message,
              });
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form-component',
                sourceArea: cartTrackingBase.sourceArea,
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = activeSubmitButton.querySelector('.sold-out-message');
              if (shouldLockButton && soldOutMessage) {
                activeSubmitButton.setAttribute('aria-disabled', true);
                this.submitButtonText?.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
              }
              return;
            } else if (isBuyNowSubmit) {
              this.dispatchCartTrackingEvent('evolova:cart-add-success', {
                ...cartTrackingBase,
                response,
                source: 'buy_now_cart_add',
              });
              this.dispatchCartTrackingEvent('evolova:checkout-handoff', {
                ...cartTrackingBase,
                response,
                source: 'buy_now',
              });
              redirectingToCheckout = true;
              this.redirectAfterTracking(`${window.routes?.root_url || '/'}checkout`);
              return;
            } else if (submitter?.dataset?.cartRedirect === 'true') {
              this.dispatchCartTrackingEvent('evolova:cart-add-success', {
                ...cartTrackingBase,
                response,
                source: 'sticky_cart_redirect',
              });
              redirectingToCheckout = true;
              this.redirectAfterTracking(`${window.routes?.cart_url || '/cart'}`);
              return;
            } else if (!this.cart) {
              this.dispatchCartTrackingEvent('evolova:cart-add-success', {
                ...cartTrackingBase,
                response,
                source: 'cart_add_redirect',
              });
              this.redirectAfterTracking(`${window.routes?.cart_url}`);
              return;
            }

            this.dispatchCartTrackingEvent('evolova:cart-add-success', {
              ...cartTrackingBase,
              response,
              source: 'cart_add_drawer',
            });

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form-component',
	                sourceArea: cartTrackingBase.sourceArea,
                  actionId: cartTrackingBase.actionId,
	                productVariantId: formData.get('id'),
	                cartData: response,
	              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener('modalClosed', () => {
                setTimeout(() => {
                  if (this.cart && typeof this.cart.renderContents === 'function') {
                    this.cart.renderContents(response);
                  }
                });
              },{ once: true });
              quickAddModal.hide(true);
            } else {
              if (this.cart && typeof this.cart.renderContents === 'function') {
                this.cart.renderContents(response);
              }
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartTrackingEvent('evolova:cart-add-error', {
              ...cartTrackingBase,
              reason: 'network_error',
              message: e?.message || String(e),
            });
          })
          .finally(() => {
            if (redirectingToCheckout) return;
            this.isSubmitting = false;

            activeSubmitButton.classList.remove('loading');
            activeSubmitButton.removeAttribute('aria-busy');
            if (!this.error) this.setSubmitButtonBusyState(activeSubmitButton, false);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) activeSubmitButton.removeAttribute('aria-disabled');
            activeSpinner?.classList.add('hidden');

            // Clear loading state on the sticky add-to-cart button (if present)
            const productInfo = this.closest('product-info');
            const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
            const stickyAddButtons = this.getStickySubmitButtons(sectionId);
            if (!isBuyNowSubmit) this.setSubmitButtonsBusyState(stickyAddButtons, false, this.error);
          });
      }

      disconnectedCallback() {
        try {
          document.removeEventListener('pointerdown', this.captureExternalSubmitter, true);
          document.removeEventListener('click', this.captureExternalSubmitter, true);
          this.clearImmediateFeedbackTimer();
          this.cartErrorUnsubscriber?.();
          this._errorObserver?.disconnect();
        } catch (e) {
          // silent
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      captureExternalSubmitter(event) {
        const target = event.target instanceof HTMLElement
          ? event.target.closest('button[type="submit"], input[type="submit"], button[name="add"], input[name="add"]')
          : null;
        if (!target) return;

        const targetForm = target.getAttribute('form');
        const formId = this.getFormId();
        if (this.form.contains(target) || (targetForm && formId && targetForm === formId)) {
          this.lastSubmitter = target;
          this.lastSubmitterAt = Date.now();
          this.applyImmediateStickyFeedback(target);
        }
      }

      getRecentExternalSubmitter() {
        if (!this.lastSubmitter || !this.lastSubmitterAt) return null;
        if (Date.now() - this.lastSubmitterAt > 2000) return null;
        return this.lastSubmitter;
      }

      getStickySubmitButtons(sectionId) {
        const formId = this.getFormId();
        if (!sectionId && !formId) return [];

        const buttons = [];
        const addButton = sectionId ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`) : null;
        const directAddButton = sectionId ? document.getElementById(`StickyCart-ProductSubmitButton-ToggleVariants-${sectionId}`) : null;
        if (addButton) buttons.push(addButton);
        if (directAddButton) buttons.push(directAddButton);

        const stickyScope = sectionId
          ? document.querySelector(`sticky-atc[data-sticky-section-id="${sectionId}"]`)
          : document.querySelector(`sticky-atc[form="${formId}"]`);
        stickyScope
          ?.querySelectorAll(`button[type="submit"][form="${formId}"], button[name="add"][form="${formId}"]`)
          .forEach((button) => {
            if (!buttons.includes(button)) buttons.push(button);
          });

        if (formId) {
          document
            .querySelectorAll(`.evo-pdp-mobile-action__button[type="submit"][form="${formId}"], .evo-pdp-mobile-action__button[name="add"][form="${formId}"]`)
            .forEach((button) => {
              if (!buttons.includes(button)) buttons.push(button);
            });
        }

        return buttons;
      }

      getFormId() {
        return this.form?.getAttribute('id') || '';
      }

      setSubmitButtonsBusyState(buttons, isBusy, keepLocked = false) {
        buttons.forEach((button) => {
          button.classList.toggle('loading', isBusy);
          button.toggleAttribute('aria-busy', isBusy);
          if (!isBusy && !keepLocked) button.removeAttribute('aria-disabled');

          const spinner = button.querySelector('.loading__spinner');
          if (spinner) spinner.classList.toggle('hidden', !isBusy);

          if (isBusy || !keepLocked) this.setSubmitButtonBusyState(button, isBusy);
        });
      }

      applyImmediateStickyFeedback(target) {
        const stickyScope = target.closest('sticky-atc');
        const formId = this.getFormId();
        const isMobileActionButton =
          target.matches('.evo-pdp-mobile-action__button') &&
          target.getAttribute('form') === formId;
        if ((!stickyScope && !isMobileActionButton) || target.disabled || target.getAttribute('aria-disabled') === 'true') return;

        const sectionId = stickyScope?.getAttribute('data-sticky-section-id');
        const buttons = this.getStickySubmitButtons(sectionId);
        if (!buttons.includes(target)) buttons.push(target);
        this.setSubmitButtonsBusyState(buttons, true);

        this.clearImmediateFeedbackTimer();
        this.immediateFeedbackTimer = window.setTimeout(() => {
          if (this.isSubmitting) return;
          this.setSubmitButtonsBusyState(buttons, false);
        }, 2500);
      }

      clearImmediateFeedbackTimer() {
        if (!this.immediateFeedbackTimer) return;
        window.clearTimeout(this.immediateFeedbackTimer);
        this.immediateFeedbackTimer = null;
      }

      finishBlockedSubmit(button, isBuyNow = false) {
        this.isSubmitting = false;
        this.clearImmediateFeedbackTimer();

        if (button) {
          button.classList.remove('loading');
          button.removeAttribute('aria-busy');
          this.setSubmitButtonBusyState(button, false);
        }

        if (isBuyNow) return;
        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
        this.setSubmitButtonsBusyState(this.getStickySubmitButtons(sectionId), false);
      }

      setSubmitButtonBusyState(button, isBusy) {
        const textElement = this.getSubmitButtonTextElement(button);
        if (!textElement) return;

        if (isBusy) {
          if (!button.dataset.evoAtcOriginalText) {
            button.dataset.evoAtcOriginalText = textElement.textContent.trim();
          }
          textElement.textContent = window.variantStrings?.addingToCart || 'Adding...';
          return;
        }

        if (!button.dataset.evoAtcOriginalText) return;
        textElement.textContent = button.dataset.evoAtcOriginalText;
        delete button.dataset.evoAtcOriginalText;
      }

      getSubmitButtonTextElement(button) {
        if (!button) return null;

        for (const child of button.children) {
          if (
            child.tagName === 'SPAN' &&
            !child.classList.contains('sold-out-message') &&
            !child.classList.contains('icon-wrap') &&
            !child.classList.contains('svg-wrapper') &&
            child.textContent.trim()
          ) {
            return child;
          }
        }

        const nestedText = button.querySelector('.add-to-cart-text, .add-to-cart-text--choose-options');
        if (nestedText) return nestedText;

        const hasDirectText = Array.from(button.childNodes).some(
          (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
        );
        if (hasDirectText && button.matches('.evo-pdp-mobile-action__button')) return button;

        return null;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getTrackingSourceArea(target, isBuyNow = false) {
        if (isBuyNow) return 'buy_now';
        return (
          target?.dataset?.sourceArea ||
          this.form?.dataset?.sourceArea ||
          this.dataset?.sourceArea ||
          'product_form'
        );
      }

      dispatchCartTrackingEvent(name, detail = {}) {
        try {
          document.dispatchEvent(
            new CustomEvent(name, {
              detail: {
                form: this.form,
                ...detail,
              },
            })
          );
        } catch (e) {
          // Tracking must never block the product form.
        }
      }

      redirectAfterTracking(url) {
        window.setTimeout(() => {
          window.location = url;
        }, 80);
      }

      createCartActionId(variantId) {
        return [
          'atc',
          variantId || 'unknown',
          Date.now(),
          Math.random().toString(36).slice(2, 8),
        ].join(':');
      }

      isInventoryLockError(response = {}) {
        const message = [
          response.message,
          response.description,
          response.errors && typeof response.errors === 'string' ? response.errors : '',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return (
          response.status === 422 &&
          /sold out|unavailable|not available|out of stock|inventory/.test(message)
        );
      }
    }
  );
}
